<template>
  <Dialog
    v-model:visible="isVisible"
    :modal="true"
    :dismissableMask="false"
    :closeOnEscape="false"
    :maximizable="false"
    :closable="true"
    :resizable="false"
    :draggable="false"
    header="Únete a Thunder Labs"
    :style="{ width: '100%', maxWidth: '500px', 'background-color': 'white' }"
    :header-style="{ 'background': 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)', 'color': 'white', 'border': 'none', 'padding': '20px' }"
    contentClass="signup-form-modal-content"
    @hide="closeModal"
  >
    <div class="signup-form-container">
      <p class="signup-form-intro">
        {{ intro || 'Comienza tu prueba gratuita hoy. Sin tarjeta de crédito requerida.' }}
      </p>
      <div id="thunderlabs-form-modal-container" ref="formContainerRef"></div>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, watch, onUnmounted, nextTick } from 'vue'
import Dialog from 'primevue/dialog'

declare global {
  interface Window {
    __thunderlabsTargetSelector?: string
  }
}

interface Props {
  visible: boolean
  intro?: string
}

const props = withDefaults(defineProps<Props>(), {
  intro: ''
})

const emit = defineEmits<{
  'update:visible': [value: boolean]
}>()

const isVisible = ref(props.visible)
let scriptTag: HTMLScriptElement | null = null
const isScriptInjected = ref(false)
const formContainerRef = ref<HTMLElement | null>(null)

watch(() => props.visible, (newVal) => {
  if (isVisible.value !== newVal) {
    console.info('[SignupFormModal] props.visible changed', { newVal })
    isVisible.value = newVal
  }
})

watch(isVisible, (newVal) => {
  console.info('[SignupFormModal] isVisible changed', { newVal })
  emit('update:visible', newVal)
  if (newVal) {
    loadEmbedScript()
  } else {
    cleanupEmbedScript()
  }
})

const loadEmbedScript = () => {
  console.info('[SignupFormModal] loadEmbedScript: start', { visible: isVisible.value })
  // Clean up previous script if exists
  if (isScriptInjected.value) {
    console.info('[SignupFormModal] loadEmbedScript: already injected, skipping')
    return
  }
  cleanupEmbedScript()

  // Wait for dialog to be rendered
  nextTick(() => {
    const container = formContainerRef.value
    console.info('[SignupFormModal] container lookup', { found: !!container })
    if (!container) {
      console.warn('Container not found for signup form')
      return
    }

    console.info('[SignupFormModal] container children before', container.childElementCount)

    // Create and inject the script
    scriptTag = document.createElement('script')
    scriptTag.src = 'https://mapp.thunderlabs.cl/f/918caa69-dab4-475d-88f5-de3105025fdc/embed.js'
    scriptTag.async = true
    scriptTag.setAttribute('data-target', '#thunderlabs-form-modal-container')
    window.__thunderlabsTargetSelector = '#thunderlabs-form-modal-container'
    scriptTag.onload = () => {
      const form = container.querySelector('form')
      isScriptInjected.value = true
      console.info('[SignupFormModal] embed.js loaded', { formFound: !!form, childCount: container.childElementCount })
    }
    scriptTag.onerror = (error) => {
      console.error('[SignupFormModal] embed.js failed to load', error)
    }
    container.appendChild(scriptTag)
    console.info('[SignupFormModal] script appended', { src: scriptTag.src })
  })
}

const cleanupEmbedScript = () => {
  console.info('[SignupFormModal] cleanupEmbedScript: start')
  if (scriptTag && scriptTag.parentNode) {
    scriptTag.parentNode.removeChild(scriptTag)
    scriptTag = null
  }
  isScriptInjected.value = false
  // Clear the container
  const container = formContainerRef.value
  if (container) {
    container.innerHTML = ''
  }
  console.info('[SignupFormModal] cleanupEmbedScript: done', { containerFound: !!container })
}

const closeModal = () => {
  isVisible.value = false
  emit('update:visible', false)
}

onUnmounted(() => {
  cleanupEmbedScript()
})
</script>

<style scoped lang="css">
.signup-form-container {
  padding: 20px 0;
}

.signup-form-intro {
  font-size: 14px;
  color: #666;
  margin-bottom: 20px;
  text-align: center;
}

:deep(.p-dialog) {
  background: white !important;
  border-radius: 8px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

:deep(.p-dialog-mask) {
  background: rgba(0, 0, 0, 0.6) !important;
  z-index: 1000;
}

:deep(.p-dialog-content) {
  background: white !important;
  padding: 20px !important;
}

:deep(.p-dialog-mask.p-component-overlay) {
  background: rgba(0, 0, 0, 0.6) !important;
}

:deep(.signup-form-modal-content) {
  background: white !important;
  padding: 20px !important;
  color: #1f2937;
}

:deep(.p-dialog-header) {
  background: linear-gradient(135deg, #f97316 0%, #ea580c 100%) !important;
  color: white !important;
  border: none !important;
  padding: 20px !important;
  border-radius: 8px 8px 0 0 !important;
}

:deep(.p-dialog-header .p-dialog-title) {
  font-weight: 600;
  font-size: 18px;
  color: white !important;
}

:deep(.p-dialog-header-icons .p-dialog-header-close) {
  color: white !important;
}

:deep(.p-dialog-header-icons .p-dialog-header-close:hover) {
  background: rgba(255, 255, 255, 0.2) !important;
  border-radius: 50%;
}

:deep(.p-dialog-header-icons) {
  gap: 0.5rem;
}
</style>
